/**
 * @module Annual Report - Slider
 * @description JS for Annual Report Template - Slider
 */
import './swiped-events.min.js';

const els = {
  sliders: null,
  tabset: null,
  currentSlider: null,
  currentTrack: null
};


const state = {
  slideWidth: null,
  currentSlide: 1,
  activeTabIndex: null,
  totalSlides: null
}


/**
 * @function tabChange
 * @description Reset our state when the tab changes.
 */
export const tabChange = ( tabIndex ) => {
  const previousTrack = els.sliders[ tabIndex - 1 ].querySelector( '.selected-region-data__slider-track' );

  if( ! previousTrack ) {
    return;
  }

  state.currentSlide = Number( previousTrack.getAttribute( 'data-current-slide' ) );
  state.slideWidth = null;
  state.activeTabIndex = null;
  state.totalSlides = Number( previousTrack.getAttribute( 'data-total-slides' ) );

  getActiveTab();
  getCurrentSlider();
  getCurrentTrack();
  setArrowState();
};


/**
 * @function setArrowState
 * @description set our deactived state for our arrow navigation
 */
const setArrowState = () => {
  const nextArrow = els.currentSlider.querySelector( '.selected-region-data__slider-nav-next' );
  const prevArrow = els.currentSlider.querySelector( '.selected-region-data__slider-nav-prev' );

  if( ! nextArrow || ! prevArrow ) {
    return;
  }

  if( state.currentSlide === 1 ) {
    prevArrow.setAttribute( 'disabled', true );
  } else {
    if( prevArrow.hasAttribute( 'disabled') ) {
      prevArrow.removeAttribute( 'disabled' );
    }

    if( state.totalSlides === state.currentSlide ) {
      nextArrow.setAttribute( 'disabled', true );
    } else {
      if( nextArrow.hasAttribute( 'disabled' ) ) {
        nextArrow.removeAttribute( 'disabled' );
      }
    }
  }
};

/**
 * @function dotActivate
 * @description set our active state for our dot navigation
 */
const dotActivate = ( dot ) => {
  dot.classList.add( '--is-active' );
};


/**
 * @function dotDeactivate
 * @description set our deactived state for our dot navigation
 */
const dotDeactivate = ( dot ) => {
  dot.classList.remove( '--is-active' );
};


/**
 * @function setDotState
 * @description set our deactived state for our dot navigation
 */
const setDotState = () => {
  const dots = els.currentSlider.querySelectorAll( '.selected-region-data__slider-dot' );
  const activeDot = els.currentSlider.querySelector( `.selected-region-data__slider-dot[data-index="${ state.currentSlide }"]` );
  const siblingDots = Array.prototype.slice.call( dots ).filter( ( dot ) => Number( dot.getAttribute( 'data-index' ) ) !== state.currentSlide );

  if( activeDot ) {
    dotActivate( activeDot );
  }

  siblingDots.forEach( ( sibling ) => {
      dotDeactivate( sibling );
  } );
};


/**
 * @function setSlideWidth
 * @description Set the current slide width
 */
const setSlideWidth = () => {
  const currentSlide = els.currentTrack.querySelector( '[data-slide]' );

  if( currentSlide ) {
    state.slideWidth = currentSlide.offsetWidth;
  }
};


/**
 * @function getActiveTab
 * @description Get the active tab in the tabset
 */
const getActiveTab = () => {
  const activeTab = els.tabset.querySelector( '.ins-annual-report__tab[data-tab-active="true"]' );

  if( activeTab ) {
    state.activeTabIndex = Number( activeTab.getAttribute( 'data-tab-content' ) - 1 );
  }
};


/**
 * @function getCurrentSlider
 * @description Get the current slider
 */
const getCurrentSlider = () => {
  els.currentSlider =  els.sliders[ state.activeTabIndex ];
};


/**
 * @function getCurrentTrack
 * @description Get the current slider track
 */
const getCurrentTrack = () => {
  const currentTrack = els.currentSlider.querySelector( '.selected-region-data__slider-track' );

  if( currentTrack ) {
    els.currentTrack = currentTrack;
  }
};


/**
 * @function getTotalSlides
 * @description Get the total number of slides within a track
 */
const getTotalSlides = () => {
  getCurrentTrack();

  if( els.currentTrack ) {
    state.totalSlides = Number( els.currentTrack.getAttribute( 'data-total-slides' ) );
  }
};


/**
 * @function moveSlider
 * @description Advance our slider
 */
const moveSlider = () => {
  els.currentTrack.setAttribute( 'data-current-slide', state.currentSlide );
  // if( state.activeTabIndex !== null && state.activeTabIndex !== 0 ) {
  //   return;
  // }

  if( ! els.currentSlider ) {
    return;
  }

  if( ! els.currentTrack ) {
    return;
  }

  setSlideWidth();

  if( ! state.slideWidth ) {
    return;
  }

  const scrollDistance = state.slideWidth * ( state.currentSlide - 1 );
  const options = {
    left: scrollDistance,
    top: 0,
    behavior: 'smooth'
  }

  els.currentTrack.scrollTo( options );
  setDotState();
  setArrowState();
};


/**
 * @function prevSlide
 * @description set to previous slide
 */
const prevSlide = () => {
  getActiveTab();
  getCurrentSlider();
  getTotalSlides();

  if( state.currentSlide === 1 ) {
    return;
  }

  state.currentSlide = state.currentSlide - 1;
  moveSlider();
};


/**
 * @function setSlide
 * @description set to previous slide
 */
const setSlide = ( event ) => {
  const { target } = event;
  const index = Number( target.parentNode.getAttribute( 'data-index' ) );

  getActiveTab();
  getCurrentSlider();
  getCurrentTrack();

  state.currentSlide = index;
  moveSlider();
};


/**
 * @function nextSlide
 * @description set to next slide
 */
const nextSlide = () => {
  getActiveTab();
  getCurrentSlider();
  getTotalSlides();

  if( state.currentSlide > state.totalSlides ) {
    return;
  }

  state.currentSlide = state.currentSlide + 1;
  moveSlider();
};


/**
 * @function bindSwipeEvents
 * @description Bind mobile finger swipe events
 */
const bindSwipeEvents = ( slider ) => {
  slider.addEventListener( 'swiped-left', nextSlide );
  slider.addEventListener( 'swiped-right', prevSlide );
};


/**
 * @function bindArrowEvents
 * @description Bind arrow events for this module
 */
const bindArrowEvents = ( nextArrow, prevArrow ) => {
  nextArrow.addEventListener( 'click', nextSlide );
  prevArrow.addEventListener( 'click', prevSlide );
}


/**
 * @function bindDotEvents
 * @description Bind dot events for this module
 */
const bindDotEvents = ( dot ) => {
  dot.addEventListener( 'click', setSlide );
}


/**
 * @function init
 * @description Kick off this modules functions
 */
const init = ( sliders, tabset ) => {
  els.sliders = sliders;
  els.tabset = tabset;

  els.sliders.forEach( ( slider ) => {
      const dots = slider.querySelectorAll( '.selected-region-data__slider-dots button' );
      dots.forEach( ( dot ) => {
          bindDotEvents( dot );
      } );

      const nextArrow = slider.querySelector( '.selected-region-data__slider-nav-next' );
      const prevArrow = slider.querySelector( '.selected-region-data__slider-nav-prev' );

      if( nextArrow && prevArrow ) {
        bindArrowEvents( nextArrow, prevArrow );
      }

      bindSwipeEvents( slider );
  } );

  getActiveTab();
  getCurrentSlider();
  getTotalSlides();
  setArrowState();
}

export default init
